/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Dashboard
 * (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../widgets/livewidget/nls/StringResources',
	'react',
	'underscore',
	'@ba/ba-ui-carbon-toolkit',
	'../grid/GridDropTarget',
	'../../../util/DashboardFormatter'
], function(StringResources, React, _, Toolkit, GridDropTarget, Formatter) {
	'use strict';

	const ROW = 'row';
	const AXIS_X = 'x';
	const AXIS_Y = 'y';
	const TABINDEX = 0;
	const EXPAND_INDENTATION_HEIGHT = 32;
	const EXPAND_INDENTATION = 24;
	const AREA_TYPE ={
		MEASURE: 'values',
		SUMMARY: 'summary'
	};
	const PROP_ID_HIDDEN_ROWS_AND_COLS = 'hiddenRowsAndColumns';
	const MIN_ROW_HEIGHT = 24;
	const MIN_COLUMN_WIDTH = 24;
	const TEXT_STYLE_INDEX = {
		CELL: 'dataValueStyles',
		CORNER: 'valueHeadingStyles',
		row: 'rowHeadingStyles',
		column: 'columnHeadingStyles',
		SUMMARY: 'summaryStyles',
		SELECTED_AREA: 'selectedAreaStyles'
	};
	const JUSTIFY_CONTENT_MAP = {
		Right: 'flex-end',
		Left: 'flex-start',
		Center: 'center'
	};

	class CrosstabCellRenderers {
		/**
         *
         * @param {object} options
         * @param {object} options.gridInfo an object that has the info of the whole grid
         * @param {int} options.gridInfo.fixedRows number of fixed rows of a grid
         * @param {int} options.gridInfo.fixedColumns number of fixed columns of a grid
         * @param {int} options.gridInfo.numRows number of rows of a grid
         * @param {int} options.gridInfo.numColumns number of columns of a grid
         * @param {int} options.gridInfo.fixedColumnWidth the default width of a fixed cell
         * @param {int} options.gridInfo.fixedRowHeight the default height of a fixed cell
         * @param {Set} options.gridInfo.coveredCellSet the set that all of its elements are cells covered by other
         *                                              cells, which means its elements don't need to be rendered
         * @param {boolean} options.gridInfo.isSuppressed true when a grid is toggled as suppressed
         * @param {Map} options.gridInfo.expandedRootMap a map whose keys are the root of expansions, values are arrays
         *                                                 of its last direct or indirect expansions leaf. We don't need
         *                                                 to know the expansions leaves therefore this map may be a set.
         *                                                  key: cellInfo.currentCellKey
         *                                                  value: [cellInfo.currentCellKey]
         * @param {Map} options.gridInfo.expandedLeafMap it's a reverse of expandedRootMap. The keys are leaves of
         *                                                  expansions and their values are an array of the direct
         *                                                  or indirect roots of an expansion leaf.
         *                                                  key: cellInfo.currentCellkey
         *                                                  value: [...JSON.stringfy({ cellKey: cellInfo.currentCellKey, resizeKey: cellInfo.resizeKey })]
         * @param {Set} options.gridInfo.renderedExpandedRootSet a set contains the root of expansions that has been rendered. Due to
         *                                                       the limitation of react-virtualized, only the cells within the current
         *                                                       visual ranges are rendered to improve the performance. However, when an
         *                                                       expansion is very long, its root maybe out of the visual range while its
         *                                                       leaves are in the range. But we still want to render the root. Therefore,
         *                                                       we use this set to check if the root is rendered.
         * @param {Map} options.gridInfo.cellProps a map that stores the cell properties passed from the callers of a (especially) root cell. Then, the
         *                                          stored cell props will be used to render a root cell that is out of the visual range.
         * @param {object} options.gridInfo.crosstabGrid an instance of the CrosstabGrid class. We have to use it for now but it should be gone later.
         * @param {object} options.gridInfo.dataProvider provides the data of a pair of given row and column index
         */
		static getFixedCellRenderer(options){
			const gridInfo = options.gridInfo;
			const crosstabGrid = gridInfo.crosstabGrid;
			const location = options.location;

			const renderer = function(rowIndex, colIndex, gridCellProps){
				let cellKey;
				let isHiddenLane;
				let hiddenZIndex;
				let deleteSize;
				let hiddenLanePosition;
				let isInVisibleRange;
				let isOutVisibleRange;
				let getOriginalCoords;
				let containerClassName;
				let offSetParent;
				let skipBoundOfColumnResizer;
				let skipBoundOfRowResizer;
				if(location === ROW){
					cellKey = `${rowIndex} ${colIndex + gridInfo.fixedColumns}`;
					isHiddenLane = (cellInfo) => {
						return crosstabGrid._cachedHiddenCol[cellInfo.currentCol];
					};
					hiddenZIndex = gridInfo.numColumns - colIndex;
					deleteSize = 'width';
					hiddenLanePosition = 'left';
					isInVisibleRange = (cellInfo, gridInfo) => {
						return gridInfo.visibleRanges && gridInfo.visibleRanges.colEnd >= cellInfo.colIndex;
					};
					getOriginalCoords = (rootCoords, gridInfo) => {
						return { row: rootCoords.row, col: rootCoords.col - gridInfo.fixedColumns };
					};
					isOutVisibleRange = (cellInfo, gridInfo) => {
						return gridInfo.visibleRanges && gridInfo.visibleRanges.colEnd <= cellInfo.colIndex;
					};
					containerClassName = 'grid-fixed-row-cell';
					offSetParent = 'ba-common-grid__top-right-grid';
					skipBoundOfColumnResizer = false;
					skipBoundOfRowResizer = true;
				}else{
					cellKey = `${rowIndex + gridInfo.fixedRows} ${colIndex}`;
					isHiddenLane = (cellInfo) => {
						return crosstabGrid._cachedHiddenRow[cellInfo.currentRow];
					};
					hiddenZIndex = gridInfo.numRows - rowIndex;
					deleteSize = 'height';
					hiddenLanePosition = 'bottom';
					isInVisibleRange = (cellInfo, gridInfo) => {
						return gridInfo.visibleRanges && gridInfo.visibleRanges.rowEnd >= cellInfo.rowIndex;
					};
					getOriginalCoords = (rootCoords, gridInfo) => {
						return { row: rootCoords.row - gridInfo.fixedRows, col: rootCoords.col };
					};
					isOutVisibleRange = (cellInfo, gridInfo) => {
						return gridInfo.visibleRanges && gridInfo.visibleRanges.rowEnd <= cellInfo.rowIndex;
					};
					containerClassName = 'grid-fixed-column-cell';
					offSetParent = 'ba-common-grid__bottom-left-grid';
					skipBoundOfColumnResizer = true;
					skipBoundOfRowResizer = false;
				}

				if(gridInfo.coveredCellSet.has(cellKey)){
					return null;
				}else{
					const cellInfo = CrosstabCellRenderers.captureFixedCellInfo(rowIndex, colIndex, gridCellProps, gridInfo, location);

					//populate the attributes we needed
					Object.assign(gridCellProps, {
						useValue: cellInfo.useValue,
						nestlevel: cellInfo.nestLevel,
						row: cellInfo.currentRow,
						col: cellInfo.currentCol,
						scope: cellInfo.scope,
						'aria-label': cellInfo.dataCellValue,
						tabindex: TABINDEX,
						area: cellInfo.area,
						measure: gridCellProps.isMeasure
					});

					if(isHiddenLane(cellInfo)){
						gridCellProps.style.zIndex = hiddenZIndex;
						delete gridCellProps.style[deleteSize];
						return React.createElement( 'div', gridCellProps, CrosstabCellRenderers.renderHiddenRowsAndColumnsHandler(cellInfo.useValue, hiddenLanePosition, gridInfo));
					}else{
						const classList = CrosstabCellRenderers.processClassName(cellInfo, gridInfo, gridCellProps, location);

						//Text toolbar style applied
						if(cellInfo.valueType) {
							const styleIndexForValueType = TEXT_STYLE_INDEX[cellInfo.valueType];
							Object.assign(gridCellProps.style, crosstabGrid.textStyle[styleIndexForValueType]);
						}

						//Selection formatting for text toolbar
						const selectedAreaStyles = crosstabGrid._getSelectionFormattingStyles(cellInfo.useValue);
						if(selectedAreaStyles){
							Object.assign(gridCellProps.style, selectedAreaStyles.convertedStylesValue);
						}

						const children = [CrosstabCellRenderers.renderTextCellOfFixedCell(cellInfo, gridInfo, location)];

						// styles overwritten for summary cell
						if (cellInfo.isSummary ){
							gridCellProps.area = AREA_TYPE.SUMMARY;
							Object.assign(gridCellProps.style, crosstabGrid.textStyle[TEXT_STYLE_INDEX.SUMMARY]);
							CrosstabCellRenderers.setSummaryColor(gridCellProps, gridInfo);
							// Set the text alignment of the text child
							children[0].props.style.justifyContent = JUSTIFY_CONTENT_MAP[gridCellProps.style['text-align']];
						} else {
							// Set the text alignment of the text child
							children[0].props.style.justifyContent = JUSTIFY_CONTENT_MAP[gridCellProps.style['text-align']];
							//render selection and implied Selection
							crosstabGrid._renderEdgeSelection(cellInfo.valueType, cellInfo.dataValue, cellInfo.useValue, classList);

							if(location === ROW){
								if (cellInfo.isMeasure) {
									children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
									children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
								} else {
									// don't add the left drop zone to an expansion leaf because its root will display the drop zone
									if (!gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)){
										children.push(React.createElement('div', { class: 'grid-drop-zone top' }, ''));
									}
									children.push(React.createElement('div', { class: 'grid-drop-zone bottom' }, ''));
								}
							}else{
								// don't add the left drop zone to an expansion leaf because its root will display the drop zone
								if (!gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)){
									children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
								}
								children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
							}
							children.push(React.createElement('div', { class: 'grid-drop-zone replace' }, ''));
						}

						// override for expanded root cell
						if (cellInfo.expandOptions.isExpanded){
							if(location === ROW){
								gridCellProps.style['align-items'] = 'flex-start';
							}else{
								gridCellProps.style.display = 'block';
								gridCellProps.style['align-items'] = 'normal';
							}
							gridInfo.renderedExpandedRootSet.add(cellInfo.currentCellKey);
							gridInfo.cellProps.set(cellInfo.currentCellKey, cellInfo.baseGridCellProps);
						}

						// for an expanded leaf cell
						let missingExpandedRoot = [];
						if (gridInfo.expandedLeafMap.has(cellInfo.currentCellKey)){
							const roots = gridInfo.expandedLeafMap.get(cellInfo.currentCellKey);
							if(isInVisibleRange(cellInfo, gridInfo)){
								roots.forEach((rootInfo) => {
									const root = JSON.parse(rootInfo);
									if(!gridInfo.renderedExpandedRootSet.has(root.cellKey)){
										const rootCellCoords = CrosstabCellRenderers.parseCellKey(root.cellKey);
										const rootCellKey = gridInfo.cellProps.get(root.cellKey);
										if(rootCellKey) {
											const rootCellProps = JSON.parse(rootCellKey.style);
											rootCellProps.onClick = gridInfo.cellProps.get(root.cellKey).onClick;
											const originalRootCoord = getOriginalCoords(rootCellCoords, gridInfo);
											const cellRenderer = CrosstabCellRenderers.getFixedCellRenderer({ gridInfo, location });
											missingExpandedRoot.push(cellRenderer(originalRootCoord.row, originalRootCoord.col, rootCellProps));
											gridInfo.renderedExpandedRootSet.add(root.cellKey);
										}
									}
								});
							}
						}

						// clean up memory
						if (isOutVisibleRange(cellInfo, gridInfo)){
							gridInfo.renderedExpandedRootSet = new Set();
						}

						gridCellProps.className = classList.join(' ');

						// common resize props
						const commonResizeHandlerOptions = {
							cellInfo,
							gridInfo,
							dataProvider: gridInfo.dataProvider,
							containerClassName: containerClassName,
							offSetParent: offSetParent,
							rowIndex: cellInfo.currentRow,
							colIndex: cellInfo.currentCol,
							colSpan: cellInfo.colSpan,
							rowSpan: cellInfo.rowSpan,
							fixedColumns: gridInfo.fixedColumns,
							fixedRows: gridInfo.fixedRows
						};

						// column resize handlers
						children.push(crosstabGrid._renderResizeHandler(Object.assign(commonResizeHandlerOptions, {
							direction: AXIS_X,
							className: 'grid-column-resizer',
							defaultValue: gridInfo.fixedColumnWidth,
							currentValue: cellInfo.gridCellColumnWidth,
							bound: MIN_COLUMN_WIDTH,
							skipBound: skipBoundOfColumnResizer
						})));

						// row resize handlers
						children.push(crosstabGrid._renderResizeHandler(Object.assign(commonResizeHandlerOptions, {
							direction: AXIS_Y,
							className: 'grid-row-resizer',
							defaultValue: gridInfo.fixedRowHeight,
							currentValue: cellInfo.gridCellRowHeight,
							bound: MIN_ROW_HEIGHT,
							skipBound: skipBoundOfRowResizer
						})));

						const fixedRowCell =  React.createElement(
							GridDropTarget,
							{ dndManager: gridInfo.gridDnDManager, cellType: cellInfo.cellType, isMeasure: cellInfo.isMeasure, ...gridCellProps },
							children
						);

						return React.createElement(React.Fragment, {}, [...missingExpandedRoot, fixedRowCell]);
					}
				}
			};
			return renderer;
		}

		/**
         * @description this function captures all of the info a header cell needs to render itself
         * @param {int} rowIndex the row index of the header grid
         * @param {int} colIndex the column index of the header grid
         * @param {object} gridCellProps the cell props passed by callers
         * @param {object} gridInfo the object that has the info of a grid
         * @param {string} location the value is 'row'(headers) or 'column'(headers)
         */
		static captureFixedCellInfo(rowIndex, colIndex, gridCellProps, gridInfo, location){
			const cellInfo = {};
			let ignoreGroupingDirection = '';
			let sizeCalculationHelper = '';
			if(location === 'row'){
				cellInfo.currentRow = rowIndex; // the row index of this cell in the whole grid
				cellInfo.currentCol = colIndex + gridInfo.fixedColumns; // the col index of this cell in the whole grid
				cellInfo.rowBound = gridInfo.fixedRows; // the row limitation
				cellInfo.colBound = gridInfo.numColumns + gridInfo.fixedColumns - 1; // the column limitation
				cellInfo.scope = 'col'; // this header will impact a whole column
				cellInfo.cellType = 'row';
				cellInfo.dataValue = gridInfo.dataProvider.getValue(cellInfo.currentRow, cellInfo.currentCol);
				cellInfo.isMeasure = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'isMeasure');
				cellInfo.area = 'cell_edge_column';
				cellInfo.valueType = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'type');
				if (cellInfo.isMeasure && cellInfo.valueType === 'column') {
					cellInfo.valueType = 'corner';
					cellInfo.area = 'values';
				}
				ignoreGroupingDirection = 'row';
				sizeCalculationHelper = CrosstabCellRenderers.calculateFixedRowCellSize;
			}else{
				cellInfo.currentRow = rowIndex + gridInfo.fixedRows; // the row index of this cell in the whole grid
				cellInfo.currentCol = colIndex; // the col index of this cell in the whole grid
				cellInfo.rowBound = gridInfo.numRows + gridInfo.fixedRows - 1; // the row limitation
				cellInfo.colBound = gridInfo.fixedColumns; // the column limitation
				cellInfo.scope = 'row';
				cellInfo.cellType = 'column';
				cellInfo.area = 'cell_edge_row';
				ignoreGroupingDirection = 'columnrow';
				// @todo For 11.2.1 Release remove the need to call CrosstabCellRenderers.calculateFixedColumnCellSize
				// Instead set css as .dashboard-common-grid {
				//		.dashboard-grid-cell {
				//			box-sizing: border-box;
				//		}
				// 	}
				// Since this function deduct 1 px for width and 1 px for height and there is no need to do this if use
				// box-sizing: border-box
				sizeCalculationHelper = CrosstabCellRenderers.calculateFixedColumnCellSize;
			}
			cellInfo.rowIndex = rowIndex;
			cellInfo.colIndex = colIndex;
			cellInfo.baseGridCellProps = {
				style: JSON.stringify(gridCellProps),
				onClick: gridCellProps.onClick
			};
			cellInfo.currentCellKey = `${cellInfo.currentRow} ${cellInfo.currentCol}`;
			cellInfo.dataValue = cellInfo.dataValue || gridInfo.dataProvider.getValue(cellInfo.currentRow, cellInfo.currentCol);
			cellInfo.resizeKey = CrosstabCellRenderers.buildResizeKey(cellInfo.dataValue['ancestor_or_self']);
			cellInfo.isSummary = CrosstabCellRenderers.isSummary(cellInfo.dataValue);
			cellInfo.useValue = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'useValue');
			cellInfo.valueType = cellInfo.valueType || CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'type');
			cellInfo.dataCellValue = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue);
			cellInfo.nestLevel = CrosstabCellRenderers.getDataCellValue(cellInfo.dataValue, 'nestlevel');

			const spanInfo = gridInfo.crosstabGrid._getSpanIndex({
				colIndex: cellInfo.currentCol,
				rowIndex: cellInfo.currentRow,
				colBound: cellInfo.colBound,
				rowBound: cellInfo.rowBound,
				dataProvider: gridInfo.dataProvider,
				fixedRows: gridInfo.fixedRows,
				fixedColumns: gridInfo.fixedColumns,
				dataCellValue: cellInfo.dataCellValue,
				useValue: cellInfo.useValue,
				gridCellProps,
				defaultColumnWidth: gridInfo.fixedColumnWidth,
				defaultRowHeight: gridInfo.fixedRowHeight,
				ignoreGrouping: {
					[ignoreGroupingDirection]: !cellInfo.isSummary
				},
				isSummaryCell: cellInfo.isSummary
			});
			Object.assign(cellInfo, spanInfo); // colSpan, rowSpan, gridCellColimnWidth, gridCellRowheight
			CrosstabCellRenderers.updateCoveredCellSet(cellInfo, gridInfo);

			cellInfo.isCellBelowInSummaryRow = CrosstabCellRenderers.isCellBelowInSummaryRow(
				cellInfo.isSummary ? cellInfo.currentRow + 1 : cellInfo + cellInfo.currentRow + cellInfo.rowSpan + 1,
				cellInfo.currentCol, gridInfo.dataProvider, true);

			cellInfo.expandOptions = CrosstabCellRenderers.getFixedCellExpandInfo(cellInfo, gridInfo, location);
			cellInfo.size = sizeCalculationHelper(cellInfo, gridInfo, gridCellProps);
			return cellInfo;
		}

		/**
         * @description render the text content of a fixed cell
         * @param {object} cellInfo
         * @param {object} gridInfo
         * @param {string} location
         */
		static renderTextCellOfFixedCell(cellInfo, gridInfo, location){
			const style = {};
			const textContentStyle = {};
			const classList = ['dashboard-text-cell'];
			let paddingRightClass;
			let paddingRightStyle;
			let paddingBottomClass;
			let paddingBottomStyle;
			if(location === ROW){
				paddingRightClass = 'row-header-padding-right';
				paddingRightStyle = {
					position: 'absolute',
					left: cellInfo.size.baseWidth - 3,
					width: cellInfo.size.totalWidth - cellInfo.size.baseWidth,
					height: EXPAND_INDENTATION_HEIGHT
				};
				paddingBottomClass = 'row-header-padding-bottom';
				paddingBottomStyle = {
					position: 'absolute',
					left: 0,
					top: EXPAND_INDENTATION_HEIGHT,
					width: cellInfo.size.baseWidth,
					height: cellInfo.size.totalHeight - EXPAND_INDENTATION_HEIGHT
				};
				style['padding-top'] = 0;
				textContentStyle['min-height'] = gridInfo.crosstabGrid.textStyle.columnHeadingStyles['font-size'];
			}else{
				paddingRightClass = 'column-header-padding-right';
				paddingRightStyle = {
					position: 'absolute',
					left: EXPAND_INDENTATION - 3,
					width: cellInfo.size.totalWidth - EXPAND_INDENTATION,
					height: cellInfo.size.baseHeight
				};
				paddingBottomClass = 'column-header-padding-bottom';
				paddingBottomStyle = {
					position: 'absolute',
					left: 0,
					top: cellInfo.size.baseHeight,
					width: EXPAND_INDENTATION,
					height: cellInfo.size.totalHeight - cellInfo.size.baseHeight
				};
				style['padding-top'] = 0;
				textContentStyle['min-height'] = gridInfo.crosstabGrid.textStyle.rowHeadingStyles['font-size'];
			}
			let formatSpec = (cellInfo.isSummary) ? null : ((cellInfo.dataValue) ? cellInfo.dataValue.formatSpec : null);
			const formattedDisplayValue = gridInfo.isSuppressed ? Formatter.format(cellInfo.dataCellValue, formatSpec) : Formatter.formatNull(cellInfo.dataCellValue);

			const textCellChildren = [];
			if(cellInfo.expandOptions.levelNumber !== undefined){
				const expandCollapseHandler = cellInfo.expandOptions.isExpanded ? CrosstabCellRenderers.renderCollapseButton(cellInfo.expandOptions, gridInfo) : CrosstabCellRenderers.renderExpandButton(cellInfo.expandOptions, gridInfo);
				textCellChildren.push(expandCollapseHandler);
			}

			let paddingRight = null;
			let paddingBottom = null;
			style['display'] = 'flex';
			style['align-items'] = 'center';
			if(cellInfo.expandOptions.isExpanded){
				Object.assign(textContentStyle, {
					overflow: 'hidden',
					'text-overflow': 'ellipsis'
				});
				const textContent = React.createElement('div', {
					style: textContentStyle
				}, [formattedDisplayValue]);
				textCellChildren.push(textContent);

				style['padding-bottom'] = 0;
				style['box-sizeing'] = 'border-box';
				style['flex'] = 'none';
				delete style['padding-top'];
				classList.push('expanded');

				paddingRight = React.createElement('div', {
					class: paddingRightClass,
					style: paddingRightStyle
				});
				paddingBottom = React.createElement('div', {
					class: paddingBottomClass,
					style: paddingBottomStyle
				});
				textCellChildren.push(paddingRight, paddingBottom);
			} else {
				style['height'] = '100%';
				Object.assign(textContentStyle, {
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					overflow: 'hidden'
				});
				const textValue = <span style={{
					overflow: 'hidden',
					'text-overflow': 'ellipsis',
					'line-height': '1.2'
				}}>{formattedDisplayValue}</span>;
				const textContent = React.createElement('div', {
					style: textContentStyle
				}, [textValue]);
				textCellChildren.push(textContent);
			}
			const textCell = React.createElement('div', {
				class: classList.join(' '),
				style: style
			}, textCellChildren);
			return textCell;
		}

		/**
         * @description this function calculate the actual size of a header cell
         * @param {object} cellInfo
         * @param {object} gridInfo
         * @param {object} gridCellProps
         */
		static calculateFixedRowCellSize(cellInfo, gridInfo, gridCellProps){
			const sizeInfo = {};
			/** For the width */
			gridCellProps.style.width = cellInfo.gridCellColumnWidth;
			if (cellInfo.currentCol + cellInfo.colSpan + 1 >= gridInfo.numColumns || cellInfo.isSummary) {
				gridCellProps.style.width = gridCellProps.style.width - 2;
			}
			sizeInfo.baseWidth = gridCellProps.style.width;
			if (cellInfo.expandOptions.isExpanded){
				gridCellProps.style.width += CrosstabCellRenderers.sumOfAllChildrenColumnWidths(cellInfo, gridInfo);
				if(gridInfo.crosstabGrid.columnWidthDeltas[cellInfo.resizeKey]) {
					gridCellProps.style.width += gridInfo.crosstabGrid.columnWidthDeltas[cellInfo.resizeKey];
				}
			}
			sizeInfo.totalWidth = gridCellProps.style.width;
			gridCellProps.style.width += 'px';

			/** For the height */
			gridCellProps.style.height = cellInfo.gridCellRowHeight;
			if (!cellInfo.isSummary){
				gridCellProps.style.height = Math.min(cellInfo.gridCellRowHeight, gridInfo.maxColHeight);
			}
			if (cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel > 0){
				const topOffset = (cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel) * EXPAND_INDENTATION_HEIGHT;
				gridCellProps.style.height -= topOffset;
				gridCellProps.style.top += topOffset;
			}
			sizeInfo.baseHeight = gridCellProps.style.height;
			sizeInfo.totalHeight = gridCellProps.style.height;
			gridCellProps.style.height += 'px';

			return sizeInfo;
		}

		/**
		 * @description Sum of all children widths of an expanded column header cell.
		 * @param {object} cellInfo
		 * @param {object} gridInfo
		 */
		static sumOfAllChildrenColumnWidths (cellInfo, gridInfo) {
			let expandedWidth = 0;
			if (cellInfo.expandOptions.isExpanded) {
				let colIndex = cellInfo.currentCol + 1;
				const count = cellInfo.currentCol + cellInfo.expandOptions.numberOfChildren;
				for (colIndex; colIndex <= count; colIndex++) {
					expandedWidth += gridInfo.crosstabGrid._getColumnWidth(gridInfo.dataProvider, cellInfo.currentRow, colIndex, gridInfo.fixedColumnWidth);
				}
			}
			return expandedWidth;
		}

		/**
		 * @description calculate the size of a column header cell
		 * @param {*} dataValue
		 */
		static calculateFixedColumnCellSize(cellInfo, gridInfo, gridCellProps){
			const sizeInfo = {};
			/** For the width */
			// leave room for the borders
			gridCellProps.style.width = cellInfo.gridCellColumnWidth;
			if(gridCellProps.style.width){
				gridCellProps.style.width -= 1;
			}
			// if it is a child of an expanded cell
			if(cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel > 0){
				const leftOffset = (cellInfo.expandOptions.levelNumber - cellInfo.expandOptions.rootLevel) * EXPAND_INDENTATION;
				gridCellProps.style.width -= leftOffset;
				gridCellProps.style.left += leftOffset;
			}
			sizeInfo.baseWidth = gridCellProps.style.width;
			sizeInfo.totalWidth = gridCellProps.style.width;
			gridCellProps.style.width += 'px';

			/** For the height */
			if(cellInfo.isSummary) {
				gridCellProps.style.height -= 1; // leave 1px for the top border
				if(!cellInfo.isCellBelowInSummaryRow){
					gridCellProps.style.height -= 1; // leave 1px for the bottom border
				}
			}else{
				gridCellProps.style.height = Math.min(cellInfo.gridCellRowHeight, gridInfo.maxColHeight) - 1;
			}
			// for the most bottom column header
			if (cellInfo.currentRow + cellInfo.rowSpan + 1 >= gridInfo.dataProvider.getNumRows()) {
				gridCellProps.style.height -= 1;
			}
			sizeInfo.baseHeight = gridCellProps.style.height;

			// if it is an expanded cell
			if(cellInfo.expandOptions.isExpanded){
				gridCellProps.numberOfChildren = cellInfo.expandOptions.numberOfChildren;
				gridCellProps.style.height += cellInfo.expandOptions.numberOfChildren * gridInfo.fixedRowHeight;
				if(gridInfo.crosstabGrid.rowHeightDeltas[cellInfo.resizeKey]) {
					gridCellProps.style.height += gridInfo.crosstabGrid.rowHeightDeltas[cellInfo.resizeKey];
				}
			}
			sizeInfo.totalHeight = gridCellProps.style.height;
			gridCellProps.style.height += 'px';

			return sizeInfo;
		}

		/**
         * @description find if a cell is covered by other cell. If yes, add it to the set.
         * @param {object} cellInfo
         * @param {object} gridInfo
         */
		static updateCoveredCellSet(cellInfo, gridInfo){
			for (let row = 0; row <= cellInfo.rowSpan; row++) {
				for (let col = 0; col <= cellInfo.colSpan; col++) {
					if (row !== 0 || col !== 0) {
						gridInfo.coveredCellSet.add(`${cellInfo.currentRow + row} ${cellInfo.currentCol + col}`);
					}
				}
			}
		}

		/**
		 * @description Tell if the cell below current cell is a row-summary cell
		 * @param nextRow the row of the cell going to be checked
		 * @param nextCol the col of the cell going to be checked
		 * @param dataProvider the data provider
		 * @param fixedColumn if the call comes from a fixed column header render
		 */
		static isCellBelowInSummaryRow(row, col, dataProvider, fixedColumns){
			let result;
			if(dataProvider.getNumRows() > row){
				const dataValue = dataProvider.getValue(row, col);
				result = fixedColumns ? CrosstabCellRenderers.isSummary(dataValue) : CrosstabCellRenderers.isInSummaryRow(dataValue);
			}else{
				result = false;
			}
			return result;
		}
		static isSummary(dataValue){
			if ( dataValue ) {
				if ( dataValue.rowEdge && dataValue.columnEdge ) {
					return dataValue.columnEdge.isSummary || dataValue.rowEdge.isSummary;
				} else {
					return dataValue.isSummary;
				}
			}
			return false;
		}

		static isInSummaryRow(dataValue){
			return dataValue && dataValue.rowEdge && dataValue.rowEdge.isSummary;
		}

		static getFixedCellExpandInfo(cellInfo, gridInfo, location){
			const cachedLevelInfo = gridInfo.pivotTable.getLevelInfo();
			if(location === ROW){
				// check if cell is expandable
				const siblingDataValue = cellInfo.colIndex + gridInfo.fixedColumns < gridInfo.numColumns ? gridInfo.dataProvider.getValue(cellInfo.rowIndex, cellInfo.colIndex + gridInfo.fixedColumns + 1) : null;
				// defect 298869: Disable expand/collapse on crosstab if property/attribute data item on edges
				const _cachedDataItemInfoByIndex = CrosstabCellRenderers.getCachedDataItemInfoByIndex(gridInfo.crosstabGrid._cachedColumnDataItemInfo, cellInfo.rowIndex);
				const isProperty = _cachedDataItemInfoByIndex && _cachedDataItemInfoByIndex.isProperty;
				const expandOptions = gridInfo.isExpandable && !isProperty ? CrosstabCellRenderers.getExpandOptions(cellInfo.dataValue, siblingDataValue, _cachedDataItemInfoByIndex, cachedLevelInfo.row[cellInfo.rowIndex], 'top') : {};
				if(expandOptions.isExpanded){
					expandOptions.numberOfChildren = CrosstabCellRenderers.getFixedCellExpandChildren(cellInfo, gridInfo, expandOptions, location);
				}
				return expandOptions;
			}else{
				// check if cell is expandable
				const siblingDataValue = cellInfo.rowIndex < gridInfo.numRows - 1 ? gridInfo.dataProvider.getValue(cellInfo.rowIndex + gridInfo.fixedRows + 1, cellInfo.colIndex) : null;
				// defect 298869: Disable expand/collapse on crosstab if property/attribute data item on edges
				const _cachedDataItemInfoByIndex = CrosstabCellRenderers.getCachedDataItemInfoByIndex(gridInfo.crosstabGrid._cachedRowDataItemInfo, cellInfo.colIndex);
				const isProperty = _cachedDataItemInfoByIndex && _cachedDataItemInfoByIndex.isProperty;
				const expandOptions = gridInfo.isExpandable && !isProperty ? CrosstabCellRenderers.getExpandOptions(cellInfo.dataValue, siblingDataValue, _cachedDataItemInfoByIndex, cachedLevelInfo.col[cellInfo.colIndex], 'left') : {};
				if(expandOptions.isExpanded){
					expandOptions.numberOfChildren = CrosstabCellRenderers.getFixedCellExpandChildren(cellInfo, gridInfo, expandOptions, location);
				}
				return expandOptions;
			}
		}

		static getFixedCellExpandChildren(cellInfo, gridInfo, currentExpandInfo, location){
			let numberOfChildren = 0;
			let next;
			let bound;
			let getCoord;
			let getCellInfo;
			if(location === ROW){
				next = cellInfo.currentCol + cellInfo.colSpan + 1;
				bound = cellInfo.colBound;
				getCoord = (cellInfo, next) => {return { row: cellInfo.currentRow, col: next };};
				getCellInfo = (cellInfo, next) => {
					return {
						rowIndex: cellInfo.rowIndex,
						colIndex: cellInfo.colIndex + (next - cellInfo.currentCol),
						dataValue: gridInfo.dataProvider.getValue(cellInfo.currentRow, next)
					};
				};
			}else{
				next = cellInfo.currentRow + cellInfo.rowSpan + 1;
				bound = cellInfo.rowBound;
				getCoord = (cellInfo, next) => {return { row: next, col: cellInfo.currentCol };};
				getCellInfo = (cellInfo, next) => {
					return {
						rowIndex: cellInfo.rowIndex + (next - cellInfo.currentRow),
						colIndex: cellInfo.colIndex,
						dataValue: gridInfo.dataProvider.getValue(next, cellInfo.currentCol)
					};
				};
			}
			while(next <= bound){
				const nextCellCoords = getCoord(cellInfo, next);
				const nextCellKey = `${nextCellCoords.row} ${nextCellCoords.col}`;
				const nextExpandInfo = CrosstabCellRenderers.getFixedCellExpandInfo(getCellInfo(cellInfo, next), gridInfo, location);
				const isExpandParent = currentExpandInfo.rootLevel === nextExpandInfo.rootLevel && currentExpandInfo.levelNumber < nextExpandInfo.levelNumber;
				if(isExpandParent){
					const rootInfo = JSON.stringify({
						cellKey: cellInfo.currentCellKey,
						resizeKey: cellInfo.resizeKey
					});
					if(!gridInfo.expandedLeafMap.has(nextCellKey)){
						gridInfo.expandedLeafMap.set(nextCellKey, [rootInfo]);
					} else {
						if(!gridInfo.expandedLeafMap.get(nextCellKey).includes(rootInfo)){
							gridInfo.expandedLeafMap.get(nextCellKey).push(rootInfo);
						}
					}
					if(!gridInfo.expandedRootMap.has(cellInfo.currentCellKey)){
						gridInfo.expandedRootMap.set(cellInfo.currentCellKey, [nextCellKey]);
					}
					numberOfChildren += 1;
					next += 1;
				}else{
					break;
				}
			}
			return numberOfChildren;
		}

		static processClassName(cellInfo, gridInfo, gridCellProps, location){
			const classList = gridCellProps.className ? [gridCellProps.className] : [];
			if(location === ROW){
				classList.push('grid-fixed-row-cell', 'dashboard-grid-cell');
				if(cellInfo.valueType && cellInfo.valueType === 'corner'){
					// highlight the measure header cell if multiselected for calculation
					if (gridInfo.measureHeaderDecorations.indexOf(cellInfo.useValue) > -1){
						classList.push('dashboard-grid-cell-selected');
					}
				}
				if(cellInfo.isSummary){
					classList.push('summary');
					if(gridInfo.isSummaryHighlighted){
						classList.push('dashboard-grid-cell-selected');
					}
				}
			}else{
				classList.push('grid-fixed-column-cell', 'dashboard-grid-cell');
				if (cellInfo.isCellBelowInSummaryRow && cellInfo.isSummary) {
					classList.push('above-summary-row');
				}
				if ( cellInfo.isSummary ) {
					classList.push('summary', 'summary-row', 'summary-row-top-border');
					if(!cellInfo.isCellBelowInSummaryRow){
						classList.push('summary-row-bottom-border');
					}
					if(gridInfo.isSummaryHighlighted){
						classList.push('dashboard-grid-cell-selected');
					}
				}
			}
			return classList;
		}

		/**
		 * @param {string} cellKey a string in a format `${currentRow} ${currentCol}`
		 * @returns {object} an object in a shape of { row: int, col: int }
		 */
		static parseCellKey(cellKey) {
			const keys = cellKey.split(' ');
			const row = parseInt(keys[0]);
			const col = parseInt(keys[1]);
			return { row, col };
		}

		/**
		 * render the hidden rows or columns handler
		 * @param {string} useValue
		 * @param {string} position
		 */
		static renderHiddenRowsAndColumnsHandler(useValue, position = 'bottom', gridInfo) {
			return React.createElement( 'div', {
				class: 'grid-hidden-rowsAndColumns ' + position,
				onDoubleClick: () => {
					gridInfo.content.setPropertyValue(PROP_ID_HIDDEN_ROWS_AND_COLS, gridInfo.hiddenRowsAndColumns.filter(hiddenRowOrColumn => hiddenRowOrColumn !== useValue));
				}
			}, '' );
		}

		static getDataCellValue(dataCellValue, valueType) {
			const getValue = (value) => {
				if (value === null || value === undefined) {
					return;
				}
				return value.value && (typeof value.value.label !== 'undefined') ? value.value.label : value.value;
			};
			const getUseValue = (value) => {
				if (value && value.type && (value.type === 'column' || value.type === 'row')) {
					const aTuples = value.ancestor_or_self;
					if (aTuples) {
						return _.pluck(aTuples, 'value') + '';
					}
				}
				return null;
			};
			if (valueType === 'tuple') {
				dataCellValue = dataCellValue && dataCellValue.tuple;
			} else if (valueType === 'useValue') {
				dataCellValue = dataCellValue && dataCellValue.isMeasure ? getValue(dataCellValue) : getUseValue(dataCellValue);
			} else if (valueType === 'condValue') {
				dataCellValue = dataCellValue && dataCellValue.condValue;
			} else if (valueType === 'type') {
				dataCellValue = dataCellValue && dataCellValue.type;
			} else if (valueType === 'isMeasure') {
				dataCellValue = dataCellValue && dataCellValue.isMeasure;
			} else if (valueType === 'nestlevel') {
				dataCellValue = dataCellValue && dataCellValue.nestlevel;
			} else if (valueType === 'rowEdge') {
				dataCellValue = dataCellValue && dataCellValue.rowEdge;
			} else if (valueType === 'columnEdge') {
				dataCellValue = dataCellValue && dataCellValue.columnEdge;
			} else if (valueType === 'ancestor_or_self') {
				dataCellValue = dataCellValue && dataCellValue.ancestor_or_self;
			} else if (valueType === 'levelNumber') {
				dataCellValue = dataCellValue && dataCellValue.value && dataCellValue.value.ln;
			} else if (valueType === 'parentUseValue') {
				dataCellValue = dataCellValue && dataCellValue.value && dataCellValue.value.pu;
			} else {
				const value = getValue(dataCellValue);
				// Should preserve null and 0 values, except undefined. Should allow user format the 3 types of
				// cells respectively.
				dataCellValue = value === undefined ? '' : value;
			}
			return dataCellValue;
		}

		/**
		 * get expand options for current dataCell
		 * @param {object} dataValue
		 * @param {object} siblingDataValue
		 * @param {object} dataItemInfo
		 * @param {string} position - top/left
		 * @returns {Object} expandOptions
		 */
		static getExpandOptions(dataValue, siblingDataValue, dataItemInfo, levelInfo, position) {
			const useValues = CrosstabCellRenderers.getDataCellValue(dataValue, 'useValue').split(',');
			const siblingUseValues = siblingDataValue && CrosstabCellRenderers.getDataCellValue(siblingDataValue, 'useValue').split(',');
			// in nested scenario, the useValue of the cell will contain all the edge useValues, the last one will be the one we need
			const useValue = useValues[useValues.length - 1];
			const siblingUseValue = siblingUseValues && siblingUseValues[siblingUseValues.length - 1];
			return {
				dataItemInfo: dataItemInfo,
				parentUseValue: CrosstabCellRenderers.getDataCellValue(dataValue, 'parentUseValue'),
				currentUseValue: useValue,
				levelNumber: CrosstabCellRenderers.getDataCellValue(dataValue, 'levelNumber'),
				isExpanded: !!(dataItemInfo && dataItemInfo.expandMap && dataItemInfo.expandMap[useValue]),
				position: position,
				rootLevel: levelInfo && levelInfo.rootLevelnumber,
				hasChildren: !!(siblingDataValue && (useValue === CrosstabCellRenderers.getDataCellValue(siblingDataValue, 'parentUseValue') || useValue === siblingUseValue))
			};
		}

		/**
		 * render the expand button
		 * @param {object} expandOptions
		 */
		static renderExpandButton(expandOptions, gridInfo) {
			const { dataItemInfo, parentUseValue, currentUseValue } = expandOptions;
			const expandCollapseFeature = gridInfo.content && gridInfo.content.getFeature('ExpandCollapse');
			return React.createElement( Toolkit.Button, {
				icon: gridInfo.crosstabGrid.iconsFeature.getIcon('add16'),
				iconSize: 'small',
				variant: 'icon',
				intent: 'primary',
				title: StringResources.get('expandBtnLabel'),
				hasIconOnly: false,
				onClick: (event) => {
					expandCollapseFeature.expand(dataItemInfo.dataitemUniqueId, parentUseValue, currentUseValue);
					event.stopPropagation();
				}
			});
		}

		/**
		 * render the collapse button
		 * @param {object} expandOptions
		 */
		static renderCollapseButton(expandOptions, gridInfo) {
			const { dataItemInfo, parentUseValue, currentUseValue, hasChildren } = expandOptions;
			const expandCollapseFeature = gridInfo.content && gridInfo.content.getFeature('ExpandCollapse');
			return React.createElement( Toolkit.Button, {
				icon: gridInfo.crosstabGrid.iconsFeature.getIcon('subtract'),
				iconSize: 'small',
				variant: 'icon',
				intent: 'primary',
				title: StringResources.get('collapseBtnLabel'),
				hasIconOnly: false,
				onClick: (event) => {
					expandCollapseFeature.collapse(dataItemInfo.dataitemUniqueId, parentUseValue, currentUseValue);
					event.stopPropagation();
				},
				style: hasChildren ? {} : { visibility: 'hidden' }
			});
		}

		/**
		 * Build resize key for the resize map from ancestor_or_self info
		 */
		static buildResizeKey(ancestor_or_self){
			let resizeKey = '';
			_.each(ancestor_or_self, dataItem => {
				if(dataItem.value){
					resizeKey += dataItem.value + ',';
				} else {
					resizeKey += dataItem + ',';
				}
			});
			return resizeKey.slice(0, -1);
		}

		/**
		 * get cached dataItemInfo by index
		 * @param {Object} cachedDataItemInfo
		 * @param {integer} row/column index
		 * @param {string} propertyName
		 * @returns {Object} dataItemInfo
		 */
		static getCachedDataItemInfoByIndex(cachedDataItemInfoMap, index, propertyName) {
			const cachedDataItemInfo = cachedDataItemInfoMap && cachedDataItemInfoMap[index];
			return propertyName ? cachedDataItemInfo && cachedDataItemInfo[propertyName] : cachedDataItemInfo;
		}

		static setSummaryColor(gridCellProps, gridInfo) {
			const summaryColor = gridInfo.conditionalFormatting && gridInfo.conditionalFormatting.getSummaryFillColor();
			if (summaryColor) {
				_.extend(gridCellProps.style, { 'background-color': summaryColor });
			}
		}
	}
	return CrosstabCellRenderers;
});
